/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpResponse, HttpEventType } from '@angular/common/http';

import { Router } from '@angular/router';

import { Observable, of, throwError } from 'rxjs';
import { delay, mergeMap, retryWhen, catchError, take, finalize, tap } from 'rxjs/operators';
import { BadisOauthLoginService } from '../badis-oauth-login.service';
import { GlobalVariablesService } from '../../global-variables.service';
import { ToastNotifyService } from '@app/pages/notifications/services/toast-notify.service';
import { getPublicEntryPath, navigateToPublicEntry } from '../../public-entry.util';
import { SUPPRESS_ACCESS_DENIED_REDIRECT } from './access-denied-redirect.context';

@Injectable()
export class AppInterceptor implements HttpInterceptor {

    private requests: HttpRequest<any>[] = [];

    private delayInMinutesBetweenRetries = 1000;
    private numberOfRetries = 3;
    private authorizationHeader = 'Authorization';

    constructor(
        //  @Optional() @Inject(APP_CONFIG) private _appConfig,
        private inj: Injector,
        private global: GlobalVariablesService

    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const authService = this.getAuthService();
        const userAccessToken = authService?.userAccessToken;
       

        const isDebug = this.global.isDebugMode;
        const debugStartedAt = isDebug ? performance.now() : null;
        const debugSizes: { rawBytes?: number | null; wireBytes?: number | null; waitMs?: number | null } = {};

        if (userAccessToken?.accessToken) {

            const token = userAccessToken.accessToken;
            const authHeaders: Record<string, string> = {
                Authorization: `Bearer ${token}`,
            };

            if (this.global.app?.tenantId) {
                authHeaders.TenantId = String(this.global.app.tenantId);
            }

            if (userAccessToken.companyId) {
                authHeaders.CompanyId = String(userAccessToken.companyId);
            }

            req = req.clone({
                setHeaders: authHeaders
            });

            this.requests.push(req);

            return next.handle(req)
                .pipe(
                    tap((event) => {
                        if (!isDebug || debugStartedAt === null) {
                            return;
                        }

                        if (debugSizes.waitMs === null || debugSizes.waitMs === undefined) {
                            if (event.type === HttpEventType.ResponseHeader) {
                                debugSizes.waitMs = Math.round(performance.now() - debugStartedAt);
                            }
                        }

                        if (event instanceof HttpResponse) {
                            const sizes = this.getDebugSizes(event);
                            debugSizes.rawBytes = sizes.rawBytes;
                            debugSizes.wireBytes = sizes.wireBytes;
                            debugSizes.waitMs = debugSizes.waitMs ?? Math.round(performance.now() - debugStartedAt);
                        }
                    }),
                    retryWhen(errors => errors.pipe(
                        mergeMap((error: HttpErrorResponse, retryAttempt: number) => {

                            if (retryAttempt === this.numberOfRetries - 1) {
                                console.log(`HTTP call '${req.method} ${req.url}' failed after ${this.numberOfRetries} retries.`);
                                return throwError(error); // no retry
                            }

                            switch (error.status) {
                                case 400:
                                case 404:
                                    return throwError(error); // no retry
                                case 419:
                                    break;
                            }

                            // this.removeRequest(req);
                            return of(error); // retry

                        }),

                        delay(this.delayInMinutesBetweenRetries),
                        take(this.numberOfRetries)

                    )),
                    catchError((error: any, caught: Observable<HttpEvent<any>>) => {

                        console.error({ error, caught });

                        if (error.status === 403 && !req.context.get(SUPPRESS_ACCESS_DENIED_REDIRECT)) {
                            const router = this.getRouter();
                            if (router) {
                                void router.navigate(['/accessdenied'], { queryParams: { returnUrl: router.url } });
                            }
                        }

                        if (error.status === 401) {

                            const newRequest = this.getNewAuthRequest(req, authService);
                            if (newRequest) {
                                return next.handle(newRequest);
                            }

                            const router = this.getRouter();
                            if (router) {
                                void navigateToPublicEntry(router, { replaceUrl: true });
                            } else if (typeof window !== 'undefined' && window.location) {
                                window.location.replace(getPublicEntryPath());
                            }

                        }
                        return throwError(error);
                    })
                )
                .pipe(
                    finalize(() => {
                        if (isDebug) {
                            const toastService = this.getToastNotifyService();
                            toastService?.showHttpDebugToast({
                                method: req.method,
                                url: this.formatDebugUrl(req.urlWithParams),
                                durationMs: debugStartedAt ? Math.round(performance.now() - debugStartedAt) : 0,
                                rawBytes: debugSizes.rawBytes ?? null,
                                wireBytes: debugSizes.wireBytes ?? null,
                                waitMs: debugSizes.waitMs ?? null,
                            });
                        }
                    })
                );
        } else {

            //    this.router.navigate(['/auth']);
            return next.handle(req).pipe(
                tap((event) => {
                    if (!isDebug || debugStartedAt === null) {
                        return;
                    }

                    if (debugSizes.waitMs === null || debugSizes.waitMs === undefined) {
                        if (event.type === HttpEventType.ResponseHeader) {
                            debugSizes.waitMs = Math.round(performance.now() - debugStartedAt);
                        }
                    }

                    if (event instanceof HttpResponse) {
                        const sizes = this.getDebugSizes(event);
                        debugSizes.rawBytes = sizes.rawBytes;
                        debugSizes.wireBytes = sizes.wireBytes;
                        debugSizes.waitMs = debugSizes.waitMs ?? Math.round(performance.now() - debugStartedAt);
                    }
                }),
                finalize(() => {
                    if (isDebug) {
                        const toastService = this.getToastNotifyService();
                        toastService?.showHttpDebugToast({
                            method: req.method,
                            url: this.formatDebugUrl(req.urlWithParams),
                            durationMs: debugStartedAt ? Math.round(performance.now() - debugStartedAt) : 0,
                            rawBytes: debugSizes.rawBytes ?? null,
                            wireBytes: debugSizes.wireBytes ?? null,
                            waitMs: debugSizes.waitMs ?? null,
                        });
                    }
                })
            );

        }
    }

    protected getNewAuthRequest(
        request: HttpRequest<any>,
        authService?: BadisOauthLoginService | null
    ): HttpRequest<any> | null {

        const newStoredToken = authService?.userAccessToken?.accessToken;

        const requestAccessTokenHeader = request.headers.get(this.authorizationHeader);

        if (!newStoredToken || !requestAccessTokenHeader) {
            console.log('There is no new AccessToken.', { requestAccessTokenHeader: requestAccessTokenHeader, newStoredToken: newStoredToken });
            return null;
        }

        const newAccessTokenHeader = `Bearer ${newStoredToken}`;

        if (requestAccessTokenHeader === newAccessTokenHeader) {
            console.log('There is no new AccessToken.', { requestAccessTokenHeader: requestAccessTokenHeader, newAccessTokenHeader: newAccessTokenHeader });
            return null;
        }

        return request.clone({ headers: request.headers.set(this.authorizationHeader, newAccessTokenHeader) });
    }

    private getAuthService(): BadisOauthLoginService | null {
        try {
            return this.inj.get(BadisOauthLoginService, null);
        } catch {
            return null;
        }
    }

    private getRouter(): Router | null {
        try {
            return this.inj.get(Router, null);
        } catch {
            return null;
        }
    }

    private getDebugSizes(response: HttpResponse<any>): { rawBytes?: number | null; wireBytes?: number | null } {
        return {
            rawBytes: this.getRawBytes(response.body),
            wireBytes: this.getWireBytes(response),
        };
    }

    private getWireBytes(response: HttpResponse<any>): number | null {
        const lengthHeader = response.headers.get('content-length');
        if (!lengthHeader) {
            return null;
        }
        const length = Number(lengthHeader);
        return Number.isFinite(length) ? length : null;
    }

    private getRawBytes(body: any): number | null {
        if (body === null || body === undefined) {
            return null;
        }

        if (body instanceof ArrayBuffer) {
            return body.byteLength;
        }

        if (typeof Blob !== 'undefined' && body instanceof Blob) {
            return body.size;
        }

        if (typeof body === 'string') {
            return this.estimateTextBytes(body);
        }

        try {
            return this.estimateTextBytes(JSON.stringify(body));
        } catch {
            return null;
        }
    }

    private estimateTextBytes(text: string): number {
        if (typeof TextEncoder !== 'undefined') {
            return new TextEncoder().encode(text).length;
        }
        return text.length;
    }

    private formatDebugUrl(url: string): string {
        const withoutProtocol = url.replace(/^https?:\/\//i, '');
        const pathStart = withoutProtocol.indexOf('/');
        const path = pathStart >= 0 ? withoutProtocol.slice(pathStart) : withoutProtocol;
        return path.split('?')[0];
    }

    private getToastNotifyService(): ToastNotifyService | null {
        try {
            return this.inj.get(ToastNotifyService, null);
        } catch {
            return null;
        }
    }

}
